import React from "react";
import { Box, Container, Typography, Grid, IconButton, Link } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useTranslation } from "react-i18next";

const Footer = () => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();

    const navLinks = [
        { label: t("footer.quickLinks.home"), href: "#hero" },
        { label: t("footer.quickLinks.about"), href: "#about" },
        { label: t("footer.quickLinks.ourStory"), href: "#our-story" },
        { label: t("footer.quickLinks.services"), href: "#services" },
        { label: t("footer.quickLinks.products"), href: "#products" },
        { label: t("footer.quickLinks.contact"), href: "#contact" },
    ];

    const socialLinks = [
        { icon: <FacebookIcon sx={{ fontSize: "1.05rem" }} />, href: "#", label: "Facebook" },
        { icon: <InstagramIcon sx={{ fontSize: "1.05rem" }} />, href: "#", label: "Instagram" },
        { icon: <WhatsAppIcon sx={{ fontSize: "1.05rem" }} />, href: "#", label: "WhatsApp" },
    ];

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                background: "#000000",
                borderTop: "1px solid rgba(255, 255, 255, 0.18)",
                color: "#ffffff",
                pt: { xs: "4rem", md: "6rem" },
                pb: { xs: "2rem", md: "2.5rem" },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(ellipse at 30% 0%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)",
                    pointerEvents: "none",
                }}
            />

            <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 3, md: 6 } }}>
                <Grid container spacing={{ xs: 4, md: 6 }} sx={{ mb: { xs: 4, md: 6 } }} alignItems="flex-start">
                    {/* Brand column */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Box
                            component="img"
                            src="/images/footer.png"
                            alt="Two Sense Hair & Beauty"
                            sx={{
                                height: { xs: "52px", md: "62px" },
                                width: "auto",
                                objectFit: "contain",
                                filter: "brightness(1.05)",
                                mb: 2.5,
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: '"Cormorant Garamond", serif',
                                fontStyle: "italic",
                                fontSize: { xs: "1.05rem", md: "1.2rem" },
                                fontWeight: 300,
                                color: "#ffffff",
                                lineHeight: 1.4,
                                maxWidth: "420px",
                            }}
                        >
                            — {t("footer.tagline")}
                        </Typography>
                    </Grid>

                    {/* Navigate */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Typography
                            sx={{
                                fontSize: "0.65rem",
                                fontWeight: 500,
                                letterSpacing: "3.5px",
                                textTransform: "uppercase",
                                color: "#ffffff",
                                mb: 2.5,
                            }}
                        >
                            {t("footer.quickLinks.title")}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                                gap: { xs: 1.25, md: 1.5 },
                            }}
                        >
                            {navLinks.map((link, idx) => (
                                <Link
                                    key={idx}
                                    href={link.href}
                                    sx={{
                                        color: "rgba(255, 255, 255, 0.7)",
                                        fontSize: "0.85rem",
                                        fontWeight: 300,
                                        textDecoration: "none",
                                        transition: "color 0.35s ease, padding-left 0.35s ease",
                                        "&:hover": {
                                            color: "#ffffff",
                                            paddingLeft: "6px",
                                        },
                                    }}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </Box>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                        pt: 3,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.75rem",
                            color: "rgba(255, 255, 255, 0.45)",
                            fontWeight: 300,
                            letterSpacing: "0.5px",
                        }}
                    >
                        © {currentYear} {t("footer.copyright")} {t("footer.rights")}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        {socialLinks.map((social, idx) => (
                            <IconButton
                                key={idx}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={social.label}
                                sx={{
                                    color: "rgba(255, 255, 255, 0.55)",
                                    width: 38,
                                    height: 38,
                                    border: "1px solid rgba(255, 255, 255, 0.25)",
                                    borderRadius: 0,
                                    transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                                    "&:hover": {
                                        color: "#000000",
                                        background: "#ffffff",
                                        borderColor: "#ffffff",
                                    },
                                }}
                            >
                                {social.icon}
                            </IconButton>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
